(function(){
  const STORAGE_KEY = 'transai_note_funnel_state_v1';

  const FIELD_KEYS = [
    'industry_segment',
    'primary_use_case',
    'current_tool',
    'privacy_need',
    'documentation_frequency',
    'purchase_quantity_tier',
    'next_step_intent'
  ];

  const UTM_KEYS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_adset',
    'utm_ad',
    'fbclid'
  ];

  const LABELS = {
    industry_segment:{
      medical:'Healthcare',
      legal:'Legal',
      casework:'Public sector or casework',
      enterprise:'Business or internal teams',
      education:'Education or research',
      other:'Other professional setting'
    },
    primary_use_case:{
      meeting_notes:'Meeting notes',
      client_conversations:'Client or customer conversations',
      interviews_fieldwork:'Interviews or field documentation',
      privacy_sensitive:'Privacy-sensitive documentation'
    },
    current_tool:{
      handwritten_then_type:'Handwritten notes, then typed later',
      phone_recording_manual:'Recording first, then organize later',
      typing_live:'Typing live during the conversation',
      cloud_ai_tool:'General AI or transcription tool'
    },
    privacy_need:{
      cannot_use_cloud:'We cannot use cloud AI tools',
      prefer_on_device:'We strongly prefer local or on-device processing',
      sensitive_need_control:'Sensitive information means we need more control',
      privacy_not_main_blocker:'Privacy matters, but workflow friction is bigger'
    },
    documentation_frequency:{
      daily:'Daily or almost daily',
      several_weekly:'Several times per week',
      weekly:'About once a week',
      occasional_important:'Occasionally, but each case matters'
    },
    purchase_quantity_tier:{
      A1:'1–3 people',
      A2:'3–10 people',
      A3:'10+ people',
      unknown:'Not sure yet'
    },
    next_step_intent:{
      workflow_recommendation:'A workflow recommendation',
      book_demo:'A product demo',
      pricing_requirements:'Pricing or rollout information',
      send_materials:'Materials first'
    }
  };

  const TRACKING = {
    stepEvents:{
      q1:'funnel_q1_view',
      q2:'funnel_q2_view',
      q3:'funnel_q3_view',
      'transition-current':'funnel_transition_1',
      q4:'funnel_q4_view',
      'transition-privacy':'funnel_transition_2',
      q5:'funnel_q5_view',
      q6:'funnel_q6_view',
      q7:'funnel_q7_view',
      lead:'funnel_lead_view',
      success:'funnel_success_view'
    },
    answerEvents:{
      industry_segment:'funnel_q1_answer',
      primary_use_case:'funnel_q2_answer',
      current_tool:'funnel_q3_answer',
      privacy_need:'funnel_q4_answer',
      documentation_frequency:'funnel_q5_answer',
      purchase_quantity_tier:'funnel_q6_answer',
      next_step_intent:'funnel_q7_answer'
    },
    answerTags:{
      industry_segment:'q1_industry',
      primary_use_case:'q2_workflow',
      current_tool:'q3_capture_method',
      privacy_need:'q4_privacy',
      documentation_frequency:'q5_frequency',
      purchase_quantity_tier:'q6_team_size',
      next_step_intent:'q7_next_step'
    },
    reachedTags:{
      q1:'reached_q1',
      q2:'reached_q2',
      q3:'reached_q3',
      'transition-current':'reached_transition_1',
      q4:'reached_q4',
      'transition-privacy':'reached_transition_2',
      q5:'reached_q5',
      q6:'reached_q6',
      q7:'reached_q7',
      lead:'reached_lead_form',
      success:'reached_success'
    }
  };

  let state = loadState();
  const trackedMilestones = new Set();

  /* =========================
     TRACKING HELPERS
     ========================= */

  const clarityQueue = [];
  const metaQueue = [];
  let clarityFlushAttempts = 0;
  let metaFlushAttempts = 0;

  function clarityCall(){
    const args = Array.prototype.slice.call(arguments);

    if(typeof window.clarity === 'function'){
      try{
        window.clarity.apply(window,args);
        return true;
      }catch(e){}
    }

    clarityQueue.push(args);
    scheduleClarityFlush();
    return false;
  }

  function scheduleClarityFlush(){
    if(clarityFlushAttempts >= 20) return;
    clarityFlushAttempts += 1;

    window.setTimeout(function(){
      if(typeof window.clarity === 'function'){
        while(clarityQueue.length){
          const args = clarityQueue.shift();
          try{ window.clarity.apply(window,args); }catch(e){}
        }
      }else{
        scheduleClarityFlush();
      }
    },500);
  }

  function clarityEvent(name){
    if(name) clarityCall('event',name);
  }

  function claritySet(key,value){
    if(!key || value === undefined || value === null || value === '') return;
    clarityCall('set',String(key),String(value).slice(0,255));
  }

  function metaCall(){
    const args = Array.prototype.slice.call(arguments);

    if(typeof window.fbq === 'function'){
      try{
        window.fbq.apply(window,args);
        return true;
      }catch(e){}
    }

    metaQueue.push(args);
    scheduleMetaFlush();
    return false;
  }

  function scheduleMetaFlush(){
    if(metaFlushAttempts >= 20) return;
    metaFlushAttempts += 1;

    window.setTimeout(function(){
      if(typeof window.fbq === 'function'){
        while(metaQueue.length){
          const args = metaQueue.shift();
          try{ window.fbq.apply(window,args); }catch(e){}
        }
      }else{
        scheduleMetaFlush();
      }
    },500);
  }

  function metaCustomEvent(name,params){
    metaCall('trackCustom',name,params || {});
  }

  function metaStandardEvent(name,params){
    metaCall('track',name,params || {});
  }

  function trackOnce(key,fn){
    if(trackedMilestones.has(key)) return;
    trackedMilestones.add(key);
    fn();
  }

  function trackPageView(){
    clarityEvent('funnel_page_view');
    claritySet('funnel_page','transai_note_general_form');
    claritySet('funnel_version','v5_tracking');

    UTM_KEYS.forEach(function(key){
      if(state.utm && state.utm[key]){
        claritySet(key,state.utm[key]);
      }
    });
  }

  function trackFunnelStart(){
    trackOnce('funnel_start',function(){
      clarityEvent('funnel_start');
      claritySet('funnel_started','yes');

      metaCustomEvent('FunnelStart',{
        funnel:'transai_note_general_form'
      });
    });
  }

  function trackStepView(stepId){
    if(!stepId || stepId === 'hero') return;

    const eventName = TRACKING.stepEvents[stepId];
    const reachedTag = TRACKING.reachedTags[stepId];

    if(eventName){
      trackOnce('step:'+stepId,function(){
        clarityEvent(eventName);
        if(reachedTag) claritySet(reachedTag,'yes');
      });
    }
  }

  function trackAnswer(field,value){
    const eventName = TRACKING.answerEvents[field];
    const tagName = TRACKING.answerTags[field];

    if(eventName) clarityEvent(eventName);
    if(tagName) claritySet(tagName,value);
  }

  function trackLeadSubmit(){
    trackOnce('lead_submit',function(){
      clarityEvent('funnel_lead_submit');
      claritySet('funnel_completed','yes');

      metaStandardEvent('Lead',{
        content_name:'TransAI Note Interactive Funnel'
      });
    });
  }

  function trackBookDemo(){
    clarityEvent('funnel_book_demo');
    claritySet('book_demo_clicked','yes');

    metaCustomEvent('BookDemoClick',{
      funnel:'transai_note_general_form'
    });
  }

  function trackFormError(type){
    if(type === 'name'){
      clarityEvent('funnel_form_error_name');
    }else if(type === 'email'){
      clarityEvent('funnel_form_error_email');
    }else if(type === 'submit'){
      clarityEvent('funnel_submit_error');
    }
  }

  /* =========================
     STATE
     ========================= */

  function defaultState(){
    return {
      industry_segment:'',
      primary_use_case:'',
      current_tool:'',
      privacy_need:'',
      documentation_frequency:'',
      purchase_quantity_tier:'',
      next_step_intent:'',
      fullName:'',
      workEmail:'',
      utm:{}
    };
  }

  function loadState(){
    try{
      const parsed = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
      return Object.assign(
        defaultState(),
        parsed,
        {utm:Object.assign({},parsed.utm || {})}
      );
    }catch(e){
      return defaultState();
    }
  }

  function saveState(){
    try{
      sessionStorage.setItem(STORAGE_KEY,JSON.stringify(state));
    }catch(e){}
  }

  function captureUTM(){
    const params = new URLSearchParams(window.location.search);

    UTM_KEYS.forEach(function(key){
      const value = params.get(key);
      if(value) state.utm[key] = value;
    });

    saveState();
  }

  function label(field,value){
    return (LABELS[field] && LABELS[field][value]) || value || '';
  }

  function screens(){
    return Array.from(document.querySelectorAll('[data-tnf-screen]'));
  }

  function isDesignMode(){
    return !!(window.Shopify && window.Shopify.designMode);
  }

  /* =========================
     SCREEN NAVIGATION
     ========================= */

  function showScreen(id){
    if(isDesignMode()) return;

    screens().forEach(function(el){
      el.classList.remove('tnf-screen--active');
    });

    const target = document.querySelector('[data-tnf-screen="'+id+'"]');

    if(target){
      target.classList.add('tnf-screen--active');

      if(target.dataset.tnfType === 'transition'){
        hydrateTransition(target);
      }

      if(target.dataset.tnfType === 'lead'){
        hydrateLead(target);
      }

      if(target.dataset.tnfType === 'success'){
        hydrateSuccess(target);
      }

      trackStepView(id);

      window.scrollTo({
        top:0,
        behavior:'smooth'
      });
    }
  }

  function hydrateQuestions(){
    document.querySelectorAll('[data-tnf-type="question"]').forEach(function(screen){
      const field = screen.dataset.tnfField;
      const selected = state[field] || '';

      screen.querySelectorAll('[data-tnf-choice]').forEach(function(choice){
        const active = choice.dataset.tnfValue === selected;

        choice.classList.toggle('is-selected',active);
        choice.setAttribute('aria-pressed',active ? 'true':'false');
      });

      const next = screen.querySelector('[data-tnf-next]');

      if(next){
        next.classList.toggle('is-disabled',!selected);
      }
    });
  }

  function hydrateTransition(screen){
    const sourceField = screen.dataset.tnfSourceField;
    const currentValue = state[sourceField];

    const rules = Array.from(screen.querySelectorAll('[data-tnf-rule]'));

    const rule =
      rules.find(function(el){
        return el.dataset.tnfMatchValue === currentValue;
      }) || rules[0];

    if(!rule) return;

    const title = screen.querySelector('[data-tnf-transition-title]');
    const body = screen.querySelector('[data-tnf-transition-body]');
    const tags = screen.querySelector('[data-tnf-transition-tags]');

    if(title) title.textContent = rule.dataset.tnfHeading || '';
    if(body) body.textContent = rule.dataset.tnfBody || '';

    if(tags){
      tags.innerHTML = '';

      [
        rule.dataset.tnfTag1,
        rule.dataset.tnfTag2,
        rule.dataset.tnfTag3
      ].forEach(function(value){
        if(value){
          const span = document.createElement('span');
          span.className = 'tnf-tag';
          span.textContent = value;
          tags.appendChild(span);
        }
      });
    }
  }

  function hydrateLead(screen){
    const nameInput = screen.querySelector('[data-tnf-name]');
    const emailInput = screen.querySelector('[data-tnf-email]');

    if(nameInput && state.fullName) nameInput.value = state.fullName;
    if(emailInput && state.workEmail) emailInput.value = state.workEmail;
  }

  function hydrateSuccess(screen){
    const name = screen.querySelector('[data-tnf-success-name]');
    const email = screen.querySelector('[data-tnf-success-email]');

    if(name){
      name.textContent = (state.fullName || 'there').split(' ')[0];
    }

    if(email){
      email.textContent = state.workEmail || 'your work email';
    }
  }

  /* =========================
     CONTACT FORM
     ========================= */

  function buildContactBody(){
    const lines = [
      'TransAI Note interactive funnel lead',
      '',
      'Contact information:',
      'Full name: ' + state.fullName,
      'Work email: ' + state.workEmail,
      '',
      'Workflow fit answers:',
      'Question 1 — Organization: ' + label('industry_segment',state.industry_segment),
      'Question 2 — Workflow to improve: ' + label('primary_use_case',state.primary_use_case),
      'Question 3 — Current capture method: ' + label('current_tool',state.current_tool),
      'Question 4 — Privacy sensitivity: ' + label('privacy_need',state.privacy_need),
      'Question 5 — Documentation frequency: ' + label('documentation_frequency',state.documentation_frequency),
      'Question 6 — Potential users: ' + label('purchase_quantity_tier',state.purchase_quantity_tier),
      'Question 7 — Most useful next step: ' + label('next_step_intent',state.next_step_intent)
    ];

    const utmEntries = Object.entries(state.utm || {});

    if(utmEntries.length){
      lines.push('','Ad attribution:');

      utmEntries.forEach(function(entry){
        lines.push(entry[0] + ': ' + entry[1]);
      });
    }

    return lines.join('\n');
  }

  function fillHiddenFields(form){
    FIELD_KEYS.forEach(function(key){
      const el = form.querySelector('[data-tnf-hidden="'+key+'"]');
      if(el) el.value = label(key,state[key]);
    });

    UTM_KEYS.forEach(function(key){
      const el = form.querySelector('[data-tnf-hidden="'+key+'"]');
      if(el) el.value = (state.utm && state.utm[key]) || '';
    });

    const body = form.querySelector('[data-tnf-contact-body]');
    if(body) body.value = buildContactBody();
  }

  function buildSuccessReturnTo(form){
    const returnInput = form.querySelector('input[name="return_to"]');

    if(!returnInput) return;

    try{
      const url = new URL(window.location.href);

      /*
        Preserve the original attribution query string,
        then add a success marker that is only returned
        after Shopify accepts the contact submission.
      */
      url.searchParams.set('tnf_submitted','1');

      returnInput.value =
        url.pathname +
        url.search;
    }catch(e){
      returnInput.value =
        window.location.pathname +
        '?tnf_submitted=1';
    }
  }


  function prepareLeadSubmission(form){
    const nameInput =
      form.querySelector('[data-tnf-name]');

    const emailInput =
      form.querySelector('[data-tnf-email]');

    const errorBox =
      form.querySelector('[data-tnf-error]');

    const submit =
      form.querySelector('[data-tnf-submit]');

    state.fullName =
      (nameInput &&
       nameInput.value ||
       '')
        .trim();

    state.workEmail =
      (emailInput &&
       emailInput.value ||
       '')
        .trim();

    if(!state.fullName){
      trackFormError('name');

      showError(
        errorBox,
        'Please enter your full name.'
      );

      if(nameInput){
        nameInput.focus();
      }

      return false;
    }

    if(
      !state.workEmail ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/
        .test(state.workEmail)
    ){
      trackFormError('email');

      showError(
        errorBox,
        'Please enter a valid work email.'
      );

      if(emailInput){
        emailInput.focus();
      }

      return false;
    }

    saveState();
    fillHiddenFields(form);
    buildSuccessReturnTo(form);
    showError(errorBox,'');

    /*
      V6.1:
      Keep Shopify's normal form submission path intact.
      The Liquid form has data-shopify-captcha="true",
      so Shopify can attach a valid h-captcha-response
      before POSTing to /contact.
    */
    if(submit){
      if(!submit.dataset.tnfOriginalLabel){
        submit.dataset.tnfOriginalLabel =
          submit.textContent.trim();
      }

      submit.textContent =
        submit.dataset.tnfSendingLabel ||
        'Submitting...';

      submit.setAttribute(
        'aria-busy',
        'true'
      );

      window.setTimeout(function(){
        if(document.documentElement.contains(submit)){
          submit.textContent =
            submit.dataset.tnfOriginalLabel ||
            'Send My Recommendation →';

          submit.removeAttribute(
            'aria-busy'
          );
        }
      },30000);
    }

    return true;
  }

  function showError(box,message){
    if(!box) return;

    box.textContent = message || '';
    box.classList.toggle('is-visible',!!message);
  }

  /* =========================
     BOOK DEMO
     ========================= */

  function bookDemo(button){
    trackBookDemo();

    const successScreen = button.closest('[data-tnf-type="success"]');

    const recipient =
      successScreen &&
      successScreen.dataset.tnfDemoRecipient ||
      'support@transai.ai';

    const subjectPrefix =
      successScreen &&
      successScreen.dataset.tnfDemoSubject ||
      'TransAI Note Demo Request';

    const lines = [
      'Hi TransAI team,',
      '',
      "I'd like to book a demo for TransAI Note.",
      '',
      'Contact information:',
      'Full name: ' + state.fullName,
      'Work email: ' + state.workEmail,
      '',
      'Workflow fit answers:',
      'Question 1 — Organization: ' + label('industry_segment',state.industry_segment),
      'Question 2 — Workflow to improve: ' + label('primary_use_case',state.primary_use_case),
      'Question 3 — Current capture method: ' + label('current_tool',state.current_tool),
      'Question 4 — Privacy sensitivity: ' + label('privacy_need',state.privacy_need),
      'Question 5 — Documentation frequency: ' + label('documentation_frequency',state.documentation_frequency),
      'Question 6 — Potential users: ' + label('purchase_quantity_tier',state.purchase_quantity_tier),
      'Question 7 — Most useful next step: ' + label('next_step_intent',state.next_step_intent),
      '',
      'Please contact me to arrange a suitable demo time.'
    ];

    const utmEntries = Object.entries(state.utm || {});

    if(utmEntries.length){
      lines.push('','Ad attribution:');

      utmEntries.forEach(function(entry){
        lines.push(entry[0] + ': ' + entry[1]);
      });
    }

    const mailto =
      'mailto:' + encodeURIComponent(recipient) +
      '?subject=' + encodeURIComponent(subjectPrefix + ' — ' + state.fullName) +
      '&body=' + encodeURIComponent(lines.join('\n'));

    window.location.href = mailto;
  }

  function resetFlow(){
    const utm = Object.assign({},state.utm || {});

    state = defaultState();
    state.utm = utm;

    saveState();
    hydrateQuestions();

    document
      .querySelectorAll('[data-tnf-name],[data-tnf-email]')
      .forEach(function(el){
        el.value = '';
      });

    trackedMilestones.clear();
    showScreen('hero');
  }

  /* =========================
     EVENTS
     ========================= */

  function bindEvents(){
    document.addEventListener('click',function(event){

      const choice = event.target.closest('[data-tnf-choice]');

      if(choice){
        const screen = choice.closest('[data-tnf-type="question"]');
        const field = screen.dataset.tnfField;

        state[field] = choice.dataset.tnfValue;
        saveState();

        screen
          .querySelectorAll('[data-tnf-choice]')
          .forEach(function(el){
            const active = el === choice;

            el.classList.toggle('is-selected',active);
            el.setAttribute('aria-pressed',active ? 'true':'false');
          });

        const next = screen.querySelector('[data-tnf-next]');
        if(next) next.classList.remove('is-disabled');

        trackAnswer(field,choice.dataset.tnfValue);
        return;
      }

      const nav = event.target.closest('[data-tnf-go]');

      if(nav){
        const target = nav.dataset.tnfGo;

        if(
          target === 'q1' &&
          nav.closest('[data-tnf-screen="hero"]')
        ){
          trackFunnelStart();
        }

        if(target) showScreen(target);
        return;
      }

      const next = event.target.closest('[data-tnf-next]');

      if(
        next &&
        !next.classList.contains('is-disabled')
      ){
        const target = next.dataset.tnfNext;
        if(target) showScreen(target);
        return;
      }

      const demo = event.target.closest('[data-tnf-book-demo]');
      if(demo){
        bookDemo(demo);
        return;
      }

      const reset = event.target.closest('[data-tnf-reset]');
      if(reset){
        resetFlow();
      }
    });

    /*
      Capture phase is intentional:
      prepare hidden funnel/UTM fields before Shopify's
      CAPTCHA submit handler completes the POST.
    */
    document.addEventListener(
      'submit',
      function(event){
        const form =
          event.target.closest(
            '[data-tnf-lead-form]'
          );

        if(!form){
          return;
        }

        const valid =
          prepareLeadSubmission(form);

        if(!valid){
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        /*
          IMPORTANT:
          Do not preventDefault() for valid submissions.
          Shopify's data-shopify-captcha="true" wiring
          must receive the normal submit flow.
        */
      },
      true
    );
  }

  /* =========================
     INIT
     ========================= */


  function isSuccessfulShopifyReturn(){
    try{
      const params =
        new URLSearchParams(
          window.location.search
        );

      return (
        params.get('tnf_submitted') === '1'
      );
    }catch(e){
      return false;
    }
  }

  function cleanSuccessMarker(){
    try{
      const url =
        new URL(
          window.location.href
        );

      url.searchParams.delete(
        'tnf_submitted'
      );

      window.history.replaceState(
        {},
        document.title,
        url.pathname +
        url.search +
        url.hash
      );
    }catch(e){}
  }

  function init(){
    captureUTM();

    const successfulReturn =
      isSuccessfulShopifyReturn();

    /*
      Avoid counting the post-submit redirect as a new
      top-of-funnel page view.
    */
    if(!successfulReturn){
      trackPageView();
    }

    bindEvents();
    hydrateQuestions();

    document
      .querySelectorAll(
        '[data-tnf-type="transition"]'
      )
      .forEach(
        hydrateTransition
      );

    if(isDesignMode()){
      screens().forEach(
        function(el){
          el.classList.add(
            'tnf-screen--active'
          );

          el.dataset.tnfDesignMode =
            'true';
        }
      );

      return;
    }

    if(successfulReturn){
      /*
        Reaching this URL marker means Shopify completed
        the contact-form POST and followed return_to.
      */
      cleanSuccessMarker();
      trackLeadSubmit();
      showScreen('success');
      return;
    }

    const active =
      document.querySelector(
        '.tnf-screen--active'
      );

    if(!active){
      showScreen('hero');
    }
  }

  if(document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded',init,{once:true});
  }else{
    init();
  }

  document.addEventListener('shopify:section:load',function(){
    state = loadState();
    hydrateQuestions();

    document
      .querySelectorAll('[data-tnf-type="transition"]')
      .forEach(hydrateTransition);
  });

})();
